import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Search,
  Users,
  Loader2,
  Calendar,
  Mail,
  MailX,
  ArrowLeft,
} from 'lucide-react';

import { useClinicianAvailability } from '../../hooks/Scraper/useClinicianAvailability';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useNavigate } from 'react-router-dom';

const SimplePracticePage = () => {
  const navigate = useNavigate();

  const {
    data: clinicianAvailability,
    loading: availabilityLoading,
    fetch: fetchClinicianAvailability,
  } = useClinicianAvailability();

  const [clinicianFilter, setClinicianFilter] = useState('');

  // Initialize data on mount
  useEffect(() => {
    if (fetchClinicianAvailability) {
      fetchClinicianAvailability({ limit: 100 });
    }
  }, []);

  const handleRefresh = useCallback(() => {
    if (fetchClinicianAvailability) fetchClinicianAvailability({ limit: 100 });
  }, [fetchClinicianAvailability]);

  // Computed stats
  const stats = useMemo(() => {
    const clinicians = clinicianAvailability || [];
    const totalClinicians = clinicians.length;
    const cliniciansWithEmail = clinicians.filter((c: any) => c.email).length;
    const totalOpenSlots = clinicians.reduce((sum: number, c: any) => sum + (c.availability?.open?.length || 0), 0);
    const totalBookedSlots = clinicians.reduce((sum: number, c: any) => sum + (c.availability?.booked?.length || 0), 0);

    return { totalClinicians, cliniciansWithEmail, totalOpenSlots, totalBookedSlots };
  }, [clinicianAvailability]);

  // Filtered clinicians
  const filteredClinicians = useMemo(() => {
    return (clinicianAvailability || []).filter((clinician: any) =>
      clinicianFilter === '' ||
      clinician.fullName?.toLowerCase().includes(clinicianFilter.toLowerCase()) ||
      clinician.firstName?.toLowerCase().includes(clinicianFilter.toLowerCase()) ||
      clinician.lastName?.toLowerCase().includes(clinicianFilter.toLowerCase())
    );
  }, [clinicianAvailability, clinicianFilter]);

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 min-h-screen">
        <div className="max-w-5xl mx-auto p-6 md:p-8 space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/practice/integrations')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/60 transition-all"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SimplePractice</h1>
                <p className="text-gray-500 text-sm">Overview of your clinician data</p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={availabilityLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${availabilityLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={<Users className="h-5 w-5 text-blue-500" />}
              value={stats.totalClinicians}
              label="Clinicians"
              loading={availabilityLoading}
            />
            <StatCard
              icon={<Mail className="h-5 w-5 text-emerald-500" />}
              value={stats.cliniciansWithEmail}
              label="With Email"
              loading={availabilityLoading}
            />
            <StatCard
              icon={<CheckCircle className="h-5 w-5 text-green-500" />}
              value={stats.totalOpenSlots}
              label="Open Slots"
              loading={availabilityLoading}
            />
            <StatCard
              icon={<Calendar className="h-5 w-5 text-purple-500" />}
              value={stats.totalBookedSlots}
              label="Booked Slots"
              loading={availabilityLoading}
            />
          </div>

          {/* Clinician List */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Clinicians</h2>
                <p className="text-sm text-gray-500 mt-0.5">{filteredClinicians.length} found</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={clinicianFilter}
                  onChange={(e) => setClinicianFilter(e.target.value)}
                  placeholder="Search clinicians..."
                  className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400 w-64"
                />
              </div>
            </div>

            {availabilityLoading && filteredClinicians.length === 0 ? (
              <div className="text-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Loading clinicians...</p>
              </div>
            ) : filteredClinicians.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium mb-1">No clinicians found</p>
                <p className="text-gray-400 text-sm">
                  {clinicianFilter
                    ? 'Try adjusting your search.'
                    : 'Sync with SimplePractice to see clinician data.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinician</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Open</th>
                      <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Booked</th>
                      <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredClinicians.map((clinician: any) => {
                      const a = clinician.availability || {};
                      const open = a.open?.length || 0;
                      const booked = a.booked?.length || 0;
                      const ooo = a.outOfOffice?.length || 0;
                      const blocked = a.blocked?.length || 0;
                      const total = open + booked + ooo + blocked;

                      return (
                        <tr key={clinician._id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-4">
                            <p className="text-sm font-medium text-gray-800">{clinician.fullName || 'Unknown'}</p>
                          </td>
                          <td className="px-5 py-4">
                            {clinician.email ? (
                              <div className="flex items-center space-x-1.5">
                                <Mail className="h-3.5 w-3.5 text-green-500" />
                                <span className="text-sm text-gray-600">{clinician.email}</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1.5">
                                <MailX className="h-3.5 w-3.5 text-gray-300" />
                                <span className="text-sm text-gray-400">No email</span>
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span className={`inline-block min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium ${
                              open > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'
                            }`}>
                              {open}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span className={`inline-block min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium ${
                              booked > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-400'
                            }`}>
                              {booked}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span className="text-sm font-medium text-gray-700">{total}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
};

// Simple stat card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: number;
  label: string;
  loading?: boolean;
}> = ({ icon, value, label, loading }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-center space-x-3">
      <div className="p-2.5 bg-gray-50 rounded-xl">{icon}</div>
      <div>
        <p className="text-xl font-bold text-gray-800">
          {loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-300" /> : value}
        </p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  </div>
);

export default SimplePracticePage;
